import type {
  FormattedResolutionDetails,
  NewResolutionStep,
  ResolutionDetailsStepperContext,
  ResolutionStep,
  ResolutionStepperIdx,
  ResolutionSteps
} from '@library/types/resolutions/resolutionDetails';
import { useCallback, useMemo, useState } from 'react';
import { resolutionSteps } from '@bo-constants/resolutions/resolutionDetailsStepper';

export const useResolutionDetailsStepper = ({
  isNewResolution,
  resolutionDetails
}: {
  isNewResolution: boolean;
  resolutionDetails: FormattedResolutionDetails;
}) => {
  const [currentNewResolutionStep, setCurrentNewResolutionStep] =
    useState<NewResolutionStep>(1);

  const currentResolutionStep: ResolutionStepperIdx = useMemo(() => {
    if (isNewResolution) {
      return currentNewResolutionStep; // 1 - Setup or 2 - Configuration
    }
    switch (resolutionDetails?.status) {
      case 'draft':
      case 'preprocessing':
      case 'ready':
      case 'unrecoverable_preprocessing_error':
        return 3; // Preprocessing
      case 'processing':
        return 4; // Processing
      case 'completed':
      case 'incomplete':
      case 'manually_completed':
      case 'unrecoverable_error':
        return 5; // Summary
      default:
        return 5;
    }
  }, [currentNewResolutionStep, isNewResolution, resolutionDetails]);

  const resolutionStepsWithStatus: ResolutionSteps = useMemo(() => {
    return resolutionSteps.map((step) => {
      let status: ResolutionStep['status'] = 'pending';

      if (step.idx < currentResolutionStep) {
        status = 'complete';
      } else if (step.idx === currentResolutionStep) {
        status = 'current';
      }

      return {
        ...step,
        status
      };
    });
  }, [currentResolutionStep]);

  const handleClickNextNewResolutionStep = useCallback(() => {
    setCurrentNewResolutionStep(2);
  }, []);

  const resolutionDetailsStepperContext: ResolutionDetailsStepperContext =
    useMemo(() => {
      return {
        currentResolutionStep,
        onClickNextNewResolutionStep: handleClickNextNewResolutionStep,
        resolutionSteps: resolutionStepsWithStatus
      };
    }, [
      currentResolutionStep,
      handleClickNextNewResolutionStep,
      resolutionStepsWithStatus
    ]);

  return {
    resolutionDetailsStepperContext
  };
};
